import { useEffect, useRef, useState } from 'react';
import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import logger from '@eneco/common/log';
import { stripAccolades } from '@eneco/common/string';
import type { RecaptchaRef } from '@eneco/components/Recaptcha/Recaptcha';
import { useFormsPublicV2PostB2BForm } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, Stack, TransitionOpacity } from '@eneco/sparky';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';

import { prepareRevolutionFormRequest } from './generic-form/request';
import { RevolutionNewsletterBackLink, RevolutionNewsletterForm } from './newsletter-form/RevolutionNewsletterForm';
import type {
  ConfiguredValue,
  NewsletterFormIntegratedWithRevolutionContent,
} from './newsletter-form-integrated-with-revolution-content.types';
import { NEWSLETTER_FORM_SLOTS, resolveSlot } from './slots';

const CONSENT_FIELD_NAME = 'msdynmkt_purposeid;channels;optinwhenchecked;msdynmkt_value';

const newsletterFormSchema = yup.object({
  emailaddress1: yup.string().email().required(),
  recaptchaToken: yup.string().required(),
  formId: yup.string().required(),
  enc_formtype: yup.string().required(),
  leadsourcecode: yup.string().required(),
  [CONSENT_FIELD_NAME]: yup.string().required(),
});

interface NewsletterFormValues {
  emailaddress1: string;
  recaptchaToken: string;
  formId: string;
  enc_formtype: string;
  leadsourcecode: string;
  [CONSENT_FIELD_NAME]: string;
  [key: string]: string;
}

// The counterpart of the legacy `getConfiguredFieldValue`: Sanity hands over the value itself,
// Sitecore the dropdown item with its value in `data`.
const configuredValue = (value?: ConfiguredValue): string => (typeof value === 'string' ? value : value?.data) ?? '';

/**
 * `NewsletterFormIntegratedWithRevolution` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/forms/src/NewsletterFormIntegrateWithRevolutionCRM/
 * NewsletterFormIntegrateWithRevolutionCRM.tsx`: an email field, the disclaimer and the reCAPTCHA notice
 * in a bordered box, posting a Revolution CRM payload with the CRM settings as hidden fields; on success
 * or failure the form gives way to the matching slot and a "back" link that resets it.
 *
 * Known simplification: the legacy component reports form funnel events through `useTracking`
 * (`trackFormInteractionStart`/`trackFormInteractionCompleted`). `lib:tracking` is not in
 * `lib:cms-components`'s allowed dependencies, so they are dropped, as in `RegisterToNewsletterForm.tsx`.
 * What is submitted is unaffected.
 *
 * Also dropped: the legacy "placeholder" labels it shows inside the Experience Editor, where this lane
 * never runs.
 */
const NewsletterFormIntegratedWithRevolution: FC = () => {
  const { adapter } = useCms();
  const content = useContent<NewsletterFormIntegratedWithRevolutionContent>();
  const rendering = useRendering();
  const { label, businessUnit } = useDC();
  const emailField = content.emailAddressFormField;

  const recaptchaRef = useRef<RecaptchaRef>(null);
  const [isTransitioning, setTransitioning] = useState(false);
  const { send, reset, error, isError, isSuccess } = useFormsPublicV2PostB2BForm();

  // The hook catches a failed call itself and reports it through `isError`/`error`, never by throwing.
  useEffect(() => {
    if (isError) logger.error('KSK1W4', 'Failed to submit form', { error });
  }, [isError, error]);

  const methods = useForm<NewsletterFormValues>({
    mode: 'onBlur',
    defaultValues: {
      formId: configuredValue(content.formIdOption),
      enc_formtype: content.encFormTypeText ?? '',
      leadsourcecode: content.leadSourceCodeText ?? '',
      [CONSENT_FIELD_NAME]: configuredValue(content.consentIdOption),
    },
    resolver: typedYupResolver<NewsletterFormValues>(newsletterFormSchema),
  });
  const {
    formState: { errors },
    register,
  } = methods;

  const introSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.intro);
  const successSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.success);
  const errorSlot = resolveSlot(rendering, NEWSLETTER_FORM_SLOTS.error);

  const submitForm: SubmitHandler<NewsletterFormValues> = async values => {
    setTransitioning(true);

    const { formId } = values;
    await send({
      businessUnit,
      formId: stripAccolades(formId),
      label,
      requestBody: prepareRevolutionFormRequest(formId, values),
      recaptchaEnterpriseToken: values.recaptchaToken,
    } as Parameters<typeof send>[0]);
    setTimeout(() => setTransitioning(false), 500);
  };

  const emailErrorMessage = (): string | undefined => {
    const fallback = errors.emailaddress1?.message;
    if (errors.emailaddress1?.type === 'required') return emailField?.requiredMessage ?? fallback;
    if (errors.emailaddress1?.type === 'email') return emailField?.validationMessage ?? fallback;
    return undefined;
  };

  const showForm = !isSuccess && !isError;

  return (
    <Box
      backgroundColor="backgroundSecondary"
      borderColor="borderDividerMediumEmphasis"
      borderRadius="m"
      borderWidth="s"
      padding={{
        initial: 6,
        lg: 10,
      }}>
      <TransitionOpacity in={!isTransitioning}>
        <Stack gap="6">
          {showForm && (
            <>
              {introSlot ? <Placeholder name={introSlot} /> : null}
              <RevolutionNewsletterForm
                methods={methods}
                onSubmit={submitForm}
                recaptchaRef={recaptchaRef}
                emailField={emailField}
                emailError={emailErrorMessage()}
                nextButtonText={content.nextButtonText}
                disclaimer={adapter.toRichText(content.disclaimerContent)}>
                <input type="hidden" {...register(CONSENT_FIELD_NAME)} />
                <input type="hidden" {...register('formId')} />
                <input type="hidden" {...register('enc_formtype')} />
                <input type="hidden" {...register('leadsourcecode')} />
              </RevolutionNewsletterForm>
            </>
          )}

          {isSuccess && successSlot ? <Placeholder name={successSlot} /> : null}
          {isError && errorSlot ? <Placeholder name={errorSlot} /> : null}

          {(isSuccess || isError) && (
            <RevolutionNewsletterBackLink
              text={content.backButtonText}
              reset={reset}
              setTransitioning={setTransitioning}
            />
          )}
        </Stack>
      </TransitionOpacity>
    </Box>
  );
};

export default NewsletterFormIntegratedWithRevolution;
